import type { RecipeStore } from '../store/types.js';
import { wirePanelRowEvents } from './panelRowEvents.js';

/**
 * IngredientsView — standalone view rendering the currently selected
 * recipe's ingredient list, independent of GraphVisualizer/ListView (a
 * third, sibling view over the same RecipeStore — see
 * docs/wiki/frontend.md). Renders into one or more containers (narrow-
 * and wide-screen copies get identical content).
 *
 * Read-only except for its rows' hover/click, both wired straight to the
 * store — see panelRowEvents.ts.
 *
 * HTML building still goes through a caller-supplied function (currently
 * GraphVisualizer.buildIngredientsPanelHtml) rather than living here —
 * see that method's own doc comment for why.
 */
export class IngredientsView {
  private containers: HTMLElement[];
  private store: RecipeStore;
  private buildHtml: (recipeId: string | null) => string;
  private onCompletePickNode?: (nodeId: string) => void;
  /** Extra per-render wiring beyond rows — e.g. the Utensilis instance's
   *  own "show hidden tools" toggle, which doesn't exist in the
   *  ingredient/preparation HTML this same class also renders. */
  private onAfterRender?: (container: HTMLElement) => void;
  // Skip rebuilding when the computed HTML hasn't actually changed — the
  // store notifies on *every* change, including hover (fired by our own
  // rows' mouseenter/mouseleave, which never affects this content). Without
  // this, hovering a row rebuilds the whole list mid-interaction, destroying
  // the very row the mouse is on and breaking the natural mouseenter/
  // mouseleave/click sequence for it and whatever's hovered next.
  private lastHtml: string | null = null;

  constructor(
    containers: HTMLElement | HTMLElement[],
    store: RecipeStore,
    buildHtml: (recipeId: string | null) => string,
    onCompletePickNode?: (nodeId: string) => void,
    onAfterRender?: (container: HTMLElement) => void,
  ) {
    this.containers = Array.isArray(containers) ? containers : [containers];
    this.store = store;
    this.buildHtml = buildHtml;
    this.onCompletePickNode = onCompletePickNode;
    this.onAfterRender = onAfterRender;

    this.store.subscribe(() => this.render());
    this.render();
  }

  private render(): void {
    const html = this.buildHtml(this.store.getSelectedRecipeId());
    if (html === this.lastHtml) return;
    this.lastHtml = html;
    for (const container of this.containers) {
      container.innerHTML = html;
      wirePanelRowEvents(container, this.store, this.onCompletePickNode);
      this.onAfterRender?.(container);
    }
  }
}
